import type { Business } from "@/types/directory";
import VideoTour from "@/components/feature/VideoTour";
import SafeImage from "@/components/base/SafeImage";
import { isPremiumPlan } from "@/utils/plans";
import { getBusinessFallbackImage } from "@/utils/fallbackImage";

interface BusinessAboutProps {
  business: Business;
}

export default function BusinessAbout({ business }: BusinessAboutProps) {
  const galleryImages = [business.cover, ...business.gallery];
  const fallbackImage = getBusinessFallbackImage(business);
  // The Video Tour widget is a Premium-plan feature — hidden for every other plan.
  const showVideo = isPremiumPlan(business.plan) && Boolean(business.videoUrl);

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
      <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
        About {business.name}
      </h2>
      <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
        {business.description}
      </p>

      <div className="mt-6">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-foreground-800">
          Services offered
        </h3>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {business.services.map((service) => (
            <span key={service} className="flex items-center gap-2 text-sm text-foreground-700">
              <span className="flex h-4 w-4 items-center justify-center text-accent-600">
                <i className="ri-checkbox-circle-line text-base" />
              </span>
              {service}
            </span>
          ))}
        </div>
      </div>

      {showVideo ? <VideoTour videoUrl={business.videoUrl} className="mt-6" /> : null}

      <div className="mt-6">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-foreground-800">
          Photo gallery
        </h3>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {galleryImages.map((image, index) => (
            <div key={`${business.slug}-gallery-${index}`} className="h-44 w-full overflow-hidden rounded-md">
              <SafeImage
                src={image}
                fallback={fallbackImage}
                alt={`${business.name} photo ${index + 1}`}
                title={`${business.name} - ${business.subcategory}`}
                className="h-full w-full object-top"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {business.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full border border-background-300 bg-background-100 px-3 py-1.5 text-xs font-medium text-foreground-700"
          >
            {tag}
          </span>
        ))}
      </div>
    </section>
  );
}